// =============================================================================
// resourceImageHtml.ts — render-time signing of resource-image <img> srcs (db-185)
// =============================================================================
// The `resource-images` bucket is PRIVATE (db-185), so an inline <img> in a
// Resources page body must carry a freshly-SIGNED url to load. Page HTML may hold
// a bare key, a legacy public url, or an (expired) signed url — resourceImageKey
// reduces any of them to the object key, and this re-signs it on every render.
//
// Non-resource <img>s and any that can't be signed are left UNCHANGED — this is
// display-only and never destructive. Pages with no <img> return untouched (the
// common case), so the transform never runs on plain text content.
// =============================================================================

import { signResourceImage } from "../data/storage";
import { resourceImageKey } from "./resourceImages";

export async function signResourceImagesInHtml(
  html: string,
  // Injectable so this is unit-testable without Supabase; defaults to the real signer.
  sign: (path: string) => Promise<string> = signResourceImage
): Promise<string> {
  const src = String(html || "");
  if (!src || typeof DOMParser === "undefined" || !src.includes("<img")) return src;

  const doc = new DOMParser().parseFromString(src, "text/html");
  const imgs = Array.from(doc.querySelectorAll("img"));
  let changed = false;
  await Promise.all(
    imgs.map(async (img) => {
      const key = resourceImageKey(img.getAttribute("src"));
      if (!key) return;
      const url = await sign(key);
      if (url) {
        img.setAttribute("src", url);
        changed = true;
      }
    })
  );
  // Only reserialize when we actually changed something, so untouched content is
  // returned byte-for-byte (no DOMParser round-trip on plain pages).
  return changed ? doc.body.innerHTML : src;
}
